/**
 * Placeholders in drafted customer messages.
 *
 * A draft marks what a person must fill in before sending as
 * `[placeholder: what to fill in]`. A message that still contains one must
 * never be sent, so every path that sends a draft checks `hasPlaceholders`.
 *
 * Each call builds its own RegExp: a shared global RegExp keeps `lastIndex`
 * between calls, so the next check of a text could start partway through it
 * and miss a placeholder.
 */

/**
 * The label reported for `[placeholder: ]`: it is still a blank to fill in, and
 * the person needs a word to look for.
 */
const EMPTY_LABEL = "blank";

/**
 * The shortest and longest bracket span `normalizePlaceholders` converts: one
 * character is a checkbox (`[x]`) or a footnote mark (`[1]`), and a span over
 * 120 characters is prose, not a field to fill in.
 */
const MIN_BRACKET_LABEL_LENGTH = 2;
const MAX_BRACKET_LABEL_LENGTH = 120;

/** `placeholder:` as it opens the brackets: any letter case, spaces allowed around the word. */
const PLACEHOLDER_KEYWORD = String.raw`[ \t]*placeholder[ \t]*:`;

/**
 * A character that makes the `[` right after it part of a word or a URL
 * (`?filters[status]=open`), not the start of a blank.
 */
const WORD_OR_URL_CHARACTER = /[\p{L}\p{N}_/?&=]/u;

/**
 * The labels of every `[placeholder: …]` in `text`, trimmed, in order, one per
 * occurrence; "blank" for one with an empty label. Every span on one line
 * counts, whatever its length. Markdown backslash escapes are read through:
 * the chat editor stores `[placeholder: price_total]` as
 * `\[placeholder: price\_total\]`, and its reader still sees the brackets.
 */
export function findPlaceholders(text: string): string[] {
  const placeholder = new RegExp(
    String.raw`\[${PLACEHOLDER_KEYWORD}([^\]\r\n]*)\]`,
    "gi",
  );
  return [...withoutMarkdownEscapes(text).matchAll(placeholder)].map(
    ([, label = ""]) => label.trim() || EMPTY_LABEL,
  );
}

/** Whether `text` still has a `[placeholder: …]` to fill in, so it must not be sent. */
export function hasPlaceholders(text: string): boolean {
  return findPlaceholders(text).length > 0;
}

/**
 * Rewrites every other square-bracket span as a placeholder, `[customer name]`
 * → `[placeholder: customer name]`, so a draft written with loose brackets is
 * caught by the same check. A span stays as written when it opens a markdown
 * link (`[text](url)`), is already a placeholder, is under 2 or over 120
 * characters, runs across lines, sits inside other brackets on its line
 * (nested or unbalanced, so what it marks is unclear), or holds no space and
 * follows a letter, digit, `_`, `/`, `?`, `&` or `=` (part of a word or a URL,
 * such as `?filters[status]=open`; a URL key or path segment never holds a
 * space, so `…/invoices/[invoice id]` is still a blank). Idempotent.
 */
export function normalizePlaceholders(text: string): string {
  const keyword = new RegExp(`^${PLACEHOLDER_KEYWORD}`, "i");
  // A span with no bracket or line break inside, not followed by a link's `(url)`.
  const bracketSpan = /\[([^[\]\r\n]*)\](?!\()/g;

  return text.replace(
    bracketSpan,
    (span: string, content: string, offset: number) => {
      const label = content.trim();
      if (label.length < MIN_BRACKET_LABEL_LENGTH) return span;
      if (label.length > MAX_BRACKET_LABEL_LENGTH) return span;
      if (keyword.test(content)) return span;
      if (
        !/\s/.test(label) &&
        WORD_OR_URL_CHARACTER.test(text.charAt(offset - 1))
      ) {
        return span;
      }
      if (isInsideBrackets(text, offset, offset + span.length)) return span;
      return `[placeholder: ${label}]`;
    },
  );
}

/**
 * `text` with every CommonMark backslash escape (`\` before ASCII punctuation)
 * replaced by the character it escapes, as a reader of the rendered text sees it.
 */
function withoutMarkdownEscapes(text: string): string {
  return text.replace(/\\([!-/:-@[-`{-~])/g, "$1");
}

/**
 * Whether `text[start, end)` sits inside other brackets on its own line: a `[`
 * before it that is still open, or a `]` after it that closes nothing.
 */
function isInsideBrackets(text: string, start: number, end: number): boolean {
  const lineStart = text.lastIndexOf("\n", start - 1) + 1;
  const lineEnd = text.indexOf("\n", end);
  const before = [...text.slice(lineStart, start)];
  const after = [
    ...text.slice(end, lineEnd === -1 ? text.length : lineEnd),
  ].reverse();
  return (
    unclosedCount(before, "[", "]") > 0 || unclosedCount(after, "]", "[") > 0
  );
}

/** Openers still unclosed after reading `chars` in order; a closer with nothing open is ignored. */
function unclosedCount(
  chars: string[],
  opener: string,
  closer: string,
): number {
  return chars.reduce((depth, char) => {
    if (char === opener) return depth + 1;
    if (char === closer) return Math.max(0, depth - 1);
    return depth;
  }, 0);
}
